import React from 'react'
import CardLayout from '../../../../components/layouts/CardLayout'
import { ReactComponent as ActivePlanVector } from '../../assets/ActivePlanVector.svg';
import { ReactComponent as ChartVector } from '../../assets/ChartVector.svg';
import { ReactComponent as ClockVector } from '../../assets/ClockVector.svg';
import { Text, useMainContext } from '../../../../modules/Dashboard';
import TickIcon from '../../../../components/icons/TickIcon';
import CalenderIcon from '../../../../components/icons/CalenderIcon';
import ClockIcon from '../../../../components/icons/ClockIcon';
import { formatDateShort, getRemainingDays } from './helper';
import InfoCircleIcon from '../../../../components/icons/InfoCircleIcon';

const CurrentUserPlan = ({ data, loading, isPlanValid }) => {
    const { main: { lang } } = useMainContext()
    if (loading) { return <CurrentUserPlanSkeleton /> }
    if (!data || !data.packageData) { return <></> }

    return (
        <CardLayout className={`!p-4 !px-6 !rounded-2xl flex flex-col xl:flex-row items-center justify-between  gap-5 ${isPlanValid ? "" : "!border-[#FF1B1B] !bg-[#270809]"}`}>
            <div className='flex justify-start gap-3  w-full'>
                <ActivePlanVector width={40} height={40} className='mb-auto' />
                <div>
                    <div className='flex gap-4 items-center'>
                        <Text tid={data.packageData.label[lang]} className={`text-3xl capitalize font-medium ${isPlanValid ? "bg-gradient-to-r from-[#C27AFF] to-[#FC6432] bg-clip-text text-transparent" : "text-[#FFA1A1]"}`} />
                        <span className={`flex items-center gap-1 px-3 py-1 rounded-lg border ${isPlanValid ? "bg-[#00C95033] border-[#00C9504D]" : "bg-[#270809] border-[#FF1B1B]"}`}>
                            {
                                isPlanValid ?
                                    <TickIcon size={12} color='#05DF72' /> :
                                    <InfoCircleIcon size={12} color='#FFA1A1' />
                            }
                            <Text tid={isPlanValid ? "your-active-plan" : "run-out"} className={`text-xs ${isPlanValid ? "text-[#05DF72]" : "text-[#FFA1A1]"}`} />
                        </span>
                    </div>
                    {
                        isPlanValid ?
                            <div className='flex gap-2 items-center flex-wrap mt-3'>
                                <span className='flex items-center gap-1'>
                                    <CalenderIcon color='#C27AFF' size={18} className="inline " />
                                    <Text tid={"activated"} className='text-[#C27AFF]' />
                                    <span className='text-[#C27AFF]'>
                                        {
                                            formatDateShort(data.packageData.createdAt, lang)
                                        }
                                    </span>
                                </span>
                                <span className='flex items-center gap-1'>
                                    <ClockIcon color='#C27AFF' size={18} className="inline " />
                                    <Text tid={"expires"} className='text-[#C27AFF]' />
                                    <span className='text-[#C27AFF]'>
                                        {
                                            formatDateShort(data.packageData.expiresAt, lang)
                                        }
                                    </span>
                                </span>
                            </div> :
                            <Text tid={"plan-deactivated-hint"} className='text-[#FFA1A1]' />
                    }
                </div>

            </div>
            {/* Analytic of plan */}
            <div className='flex lg:items-center gap-3 flex-col items-start lg:flex-row w-full'>
                <div className='plan-analytics-card analytics-green flex items-center gap-2 p-3 rounded-2xl bg-[#00C95033] border border-[#00C9504D] w-full'>
                    <ChartVector height={35} width={35} />
                    <Text tid={"analyses"} className='text-[#05DF72] text-lg' />
                    <div className='mr-auto' />
                    <span className='text-xl'>{data.packageData.maxUse - data.packageData.used}</span>
                    <Text tid={"remaining"} className='text-[#05DF72] text-sm' />
                </div>
                <div className='plan-analytics-card analytics-purple flex items-center gap-2 p-3 rounded-2xl bg-[#59168B33] border border-[#AD46FF33] w-full'>
                    <ClockVector height={35} width={35} />
                    <Text tid={"time-left"} className='text-[#C27AFF] text-lg text-nowrap' />
                    <div className='mr-auto' />
                    <span className='text-xl'>{getRemainingDays(data.packageData.expiresAt)}</span>
                    <Text tid={"days-remaining"} className='text-[#C27AFF] text-sm text-nowrap' />
                </div>
            </div>
        </CardLayout>
    )
}

export default CurrentUserPlan



const CurrentUserPlanSkeleton = () => {
    return (
        <CardLayout className="!p-4 !px-6 !rounded-2xl flex flex-col xl:flex-row items-center justify-between gap-5 animate-pulse">

            {/* LEFT SIDE */}
            <div className="flex justify-start gap-3 w-full">
                {/* Icon */}
                <div className="w-10 h-10 bg-gray-300/30 rounded-xl" />

                <div className="flex flex-col gap-3 w-full">
                    {/* Title + badge */}
                    <div className="flex gap-4 items-center">
                        <div className="h-7 w-40 bg-gray-300/30 rounded-md" />
                        <div className="h-6 w-24 bg-gray-300/30 rounded-lg" />
                    </div>

                    {/* Dates row */}
                    <div className="flex gap-4 flex-wrap">
                        <div className="h-5 w-32 bg-gray-300/30 rounded-md" />
                        <div className="h-5 w-32 bg-gray-300/30 rounded-md" />
                    </div>
                </div>
            </div>

            {/* RIGHT SIDE */}
            <div className="flex lg:items-center gap-3 flex-col items-start lg:flex-row w-full">

                {/* Analyses Box */}
                <div className="flex items-center gap-2 p-3 rounded-2xl bg-gray-300/10 border border-gray-300/20 w-full">
                    <div className="w-9 h-9 bg-gray-300/30 rounded-md" />
                    <div className="h-5 w-20 bg-gray-300/30 rounded-md" />
                    <div className="mr-auto" />
                    <div className="h-6 w-10 bg-gray-300/30 rounded-md" />
                    <div className="h-4 w-16 bg-gray-300/30 rounded-md" />
                </div>

                {/* Time Left Box */}
                <div className="flex items-center gap-2 p-3 rounded-2xl bg-gray-300/10 border border-gray-300/20 w-full">
                    <div className="w-9 h-9 bg-gray-300/30 rounded-md" />
                    <div className="h-5 w-24 bg-gray-300/30 rounded-md" />
                    <div className="mr-auto" />
                    <div className="h-6 w-10 bg-gray-300/30 rounded-md" />
                    <div className="h-4 w-24 bg-gray-300/30 rounded-md" />
                </div>

            </div>

        </CardLayout>
    );
};
